// app.auth — NATIVE Cognito authentication, no hosted UI.
//
// Every operation here is an unauthenticated call against the Cognito
// Identity Provider JSON API (`cognito-idp.{region}.amazonaws.com`) with a
// plain fetch — no AWS SDK, no SigV4, no OAuth redirect, and therefore no
// callback-URL registration (the `redirect_mismatch` class of bugs simply
// doesn't exist on this path). Tokens that come back are byte-identical in
// meaning to hosted-UI tokens: the Identity-Pool exchange, principal-tag
// enforcement, and STS scoping downstream are unchanged.
//
// The hosted UI remains provisioned but DORMANT — it's the escape hatch for
// future third-party IdP federation (Google/Apple/SAML), which is the one
// flow Cognito only offers through /oauth2/authorize. `app.signIn()` (the
// redirect) stays for that; `app.auth.*` is the default.
//
// Challenge model: sign-in may return a CHALLENGE instead of tokens —
// NEW_PASSWORD_REQUIRED (admin-created users with temp passwords, e.g. the
// bootstrap root user), SOFTWARE_TOKEN_MFA / SMS_MFA / EMAIL_OTP (when the
// Auth panel enables them). Callers loop: render the challenge, call
// respondToChallenge, repeat until `status: "ok"`.

import { type AuthFlow, createAuthFlow } from "./auth-flow.ts";
import type { OidcSession } from "./oidc.ts";

export class AuthError extends Error {
  /** Cognito exception name, e.g. `NotAuthorizedException`,
   *  `UserNotConfirmedException`, `CodeMismatchException`. */
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

/** The auth challenges the SDK plumbs through. Cognito has more; unknown
 *  ones surface with their raw name so a caller can at least show it. */
export type AuthChallenge =
  | "NEW_PASSWORD_REQUIRED"
  | "SOFTWARE_TOKEN_MFA"
  | "SMS_MFA"
  | "EMAIL_OTP"
  | "WEB_AUTHN"
  | (string & {});

export type SignInResult =
  | { status: "ok"; session: OidcSession }
  | {
      status: "challenge";
      challenge: AuthChallenge;
      /** Opaque Cognito continuation token — pass back to respondToChallenge. */
      session: string;
      username: string;
      /** Challenge-specific data Cognito sent along — WEB_AUTHN delivers its
       *  `CREDENTIAL_REQUEST_OPTIONS` here. Absent for code challenges. */
      parameters?: Record<string, string>;
    };

export interface RespondToChallengeInput {
  username: string;
  challenge: AuthChallenge;
  /** The continuation token from the previous round. */
  session: string;
  /** For code-shaped challenges (MFA / EMAIL_OTP). */
  code?: string;
  /** For NEW_PASSWORD_REQUIRED. */
  newPassword?: string;
  /** For WEB_AUTHN: the serialized AuthenticationResponseJSON from the
   *  passkey provider (`navigator.credentials.get(...).toJSON()`). */
  credential?: string;
  /** For NEW_PASSWORD_REQUIRED on pools with REQUIRED attributes the admin
   *  invite didn't set (the challenge's `requiredAttributes`). Each entry is
   *  sent as `userAttributes.{name}` per the Cognito API — without them the
   *  rotation dead-ends with InvalidParameterException. */
  userAttributes?: Record<string, string>;
}

/** A high-entropy password satisfying any pool policy, from the platform's
 *  CSPRNG. Used for PASSWORDLESS signup: Cognito requires a password on every
 *  account, but the user never sees or needs it (they sign in by code/provider).
 *  ~140 bits — unguessable — and discarded after the account is created. */
export function generateSignupPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = new Uint8Array(24);
  globalThis.crypto.getRandomValues(bytes);
  return `S3p-${[...bytes].map((b) => alphabet[b % alphabet.length]).join("")}!`;
}

export interface AuthModule {
  /** Password sign-in (`InitiateAuth USER_PASSWORD_AUTH`). On `"ok"` the
   *  session has been adopted (persisted + live on the client). */
  signIn(username: string, password: string): Promise<SignInResult>;
  /** PASSWORDLESS sign-in: ask Cognito to email a one-time code
   *  (`InitiateAuth USER_AUTH`, preferred challenge EMAIL_OTP — Essentials
   *  pools with EMAIL_OTP in AllowedFirstAuthFactors). Always returns a
   *  `"challenge"`; answer it with `respondToChallenge({ challenge:
   *  "EMAIL_OTP", code })`. */
  signInWithEmailOtp(username: string): Promise<SignInResult>;
  /** PASSKEY sign-in, end to end: requests the WEB_AUTHN challenge
   *  (`USER_AUTH`, preferred challenge WEB_AUTHN), drives the platform
   *  authenticator (`navigator.credentials.get`), and answers the challenge.
   *  Call from a user gesture. Browser must support WebAuthn JSON. */
  signInWithPasskey(username: string): Promise<SignInResult>;
  /** Enroll a passkey for the SIGNED-IN user on this device/provider
   *  (`StartWebAuthnRegistration` → `navigator.credentials.create` →
   *  `CompleteWebAuthnRegistration`). Pass the session's access token. */
  registerPasskey(accessToken: string): Promise<void>;
  /** Answer a challenge from signIn (temp-password rotation, MFA, OTP). */
  respondToChallenge(input: RespondToChallengeInput): Promise<SignInResult>;
  /** Create an account. `confirmed: false` → a verification code was
   *  emailed; call confirmSignUp with it. Verification is ENFORCED:
   *  unconfirmed accounts cannot sign in. */
  /** Omit `password` for a PASSWORDLESS account: a sealed random one is
   *  generated (`generateSignupPassword`). The returned `password` is the
   *  effective one — keep it to complete a silent sign-in right after
   *  `confirmSignUp` (so passwordless signup is ONE code). */
  signUp(
    username: string,
    password?: string,
    attributes?: Record<string, string>,
  ): Promise<{ confirmed: boolean; password: string }>;
  confirmSignUp(username: string, code: string): Promise<void>;
  resendCode(username: string): Promise<void>;
  forgotPassword(username: string): Promise<void>;
  confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void>;
  /** Mint fresh id/access tokens from a refresh token
   *  (`InitiateAuth REFRESH_TOKEN_AUTH`) — the native counterpart of the
   *  hosted-UI token endpoint. Cognito does not rotate the refresh token
   *  here; the returned session carries the one you passed in. */
  refresh(refreshToken: string): Promise<OidcSession>;
  /** Best-effort revoke of the refresh token (`RevokeToken`), then local
   *  sign-out via the configured callback. No hosted-UI redirect. */
  signOut(refreshToken?: string): Promise<void>;
  /** Which sign-in methods this app offers, from the public /__s3p/auth.json
   *  the console's Sign-in panel publishes. A sign-in UI renders exactly
   *  these. Falls back to password-only if the file is absent (older apps). */
  enabledMethods(): Promise<{
    password: boolean;
    emailCode: boolean;
    /** WEB_AUTHN is an enabled first factor — render a passkey button. */
    passkey: boolean;
    /** "email" (default): the identifier is an email address. "handle": a
     *  free-form handle on an email-less pool (no OTP, no self-reset). */
    usernameMode: "email" | "handle";
    providers: string[];
    /** Whether self-signup is allowed (policy "open"). A signup UI shows the
     *  "Sign up with {provider}" buttons only when true. Default true. */
    signupOpen: boolean;
  }>;
  /** A HEADLESS sign-in / sign-up state machine built on the methods above.
   *  Render `flow.get().step`, call the transitions — no Cognito knowledge
   *  required. See `auth-flow.ts`. Fresh machine per call. */
  flow(): AuthFlow;
}

export interface AuthConfig {
  region: string;
  clientId: string;
  /** Called with tokens whenever a flow completes — the client adopts them
   *  (persist + live state). */
  onSession: (session: OidcSession) => void | Promise<void>;
  /** Called by signOut after (best-effort) revocation. */
  onSignOut?: () => void;
  /** The hosted-UI provider redirect (i.e. `app.signIn`). Used by
   *  `flow().useProvider(name)`. Absent → the flow's provider path throws a
   *  clear error (fine for pools with no external IdP). */
  onProviderRedirect?: (name: string) => Promise<void>;
}

export function createAuth(cfg: AuthConfig): AuthModule {
  const endpoint = `https://cognito-idp.${cfg.region}.amazonaws.com/`;

  async function call<T>(target: string, body: Record<string, unknown>): Promise<T> {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "content-type": "application/x-amz-json-1.1",
        "x-amz-target": `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
    });
    const json = (await res.json().catch(() => ({}))) as {
      __type?: string;
      message?: string;
      Message?: string;
    };
    if (!res.ok) {
      const code = (json.__type ?? "").split("#").pop() || "AuthError";
      throw new AuthError(code, json.message ?? json.Message ?? `${target} failed`);
    }
    return json as T;
  }

  interface AuthResponse {
    AuthenticationResult?: {
      IdToken?: string;
      AccessToken?: string;
      RefreshToken?: string;
      ExpiresIn?: number;
      TokenType?: string;
    };
    ChallengeName?: string;
    Session?: string;
    ChallengeParameters?: Record<string, string>;
  }

  function toSession(a: NonNullable<AuthResponse["AuthenticationResult"]>, refreshToken?: string): OidcSession {
    return {
      id_token: a.IdToken ?? "",
      access_token: a.AccessToken ?? "",
      // REFRESH_TOKEN_AUTH doesn't return a refresh token — keep the caller's.
      refresh_token: a.RefreshToken ?? refreshToken,
      token_type: a.TokenType ?? "Bearer",
      expires_in: a.ExpiresIn,
    } as OidcSession;
  }

  async function settle(r: AuthResponse, username: string): Promise<SignInResult> {
    if (r.AuthenticationResult?.IdToken) {
      const session = toSession(r.AuthenticationResult);
      await cfg.onSession(session);
      return { status: "ok", session };
    }
    if (r.ChallengeName && r.Session) {
      return {
        status: "challenge",
        challenge: r.ChallengeName,
        session: r.Session,
        username,
        parameters: r.ChallengeParameters,
      };
    }
    throw new AuthError("NoTokens", "sign-in returned neither tokens nor a challenge");
  }

  // ── WebAuthn (passkey) browser glue ─────────────────────────────────
  // Cognito speaks the WebAuthn-JSON wire format; modern browsers parse and
  // serialize it natively. No manual base64url surgery — feature-detect and
  // fail with a clear error instead.
  type PKCStatic = {
    parseCreationOptionsFromJSON(json: unknown): PublicKeyCredentialCreationOptions;
    parseRequestOptionsFromJSON(json: unknown): PublicKeyCredentialRequestOptions;
  };
  function pkc(): PKCStatic {
    const c = globalThis.PublicKeyCredential as unknown as Partial<PKCStatic> | undefined;
    if (!c?.parseCreationOptionsFromJSON || !c.parseRequestOptionsFromJSON) {
      throw new AuthError(
        "WebAuthnUnsupported",
        "this browser can't use passkeys (missing WebAuthn JSON support)",
      );
    }
    return c as PKCStatic;
  }
  function credentialJson(cred: Credential | null): string {
    const c = cred as (Credential & { toJSON?: () => unknown }) | null;
    if (!c?.toJSON) {
      throw new AuthError("WebAuthnUnsupported", "passkey provider returned no serializable credential");
    }
    return JSON.stringify(c.toJSON());
  }

  const mod: AuthModule = {
    async signIn(username, password) {
      const r = await call<AuthResponse>("InitiateAuth", {
        ClientId: cfg.clientId,
        AuthFlow: "USER_PASSWORD_AUTH",
        AuthParameters: { USERNAME: username, PASSWORD: password },
      });
      return settle(r, username);
    },

    async signInWithEmailOtp(username) {
      const r = await call<AuthResponse>("InitiateAuth", {
        ClientId: cfg.clientId,
        AuthFlow: "USER_AUTH",
        AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: "EMAIL_OTP" },
      });
      return settle(r, username);
    },

    async signInWithPasskey(username) {
      // The WHOLE dance in one call: ask for the WEB_AUTHN challenge, hand its
      // CREDENTIAL_REQUEST_OPTIONS to the platform authenticator, return the
      // assertion. One user gesture, no intermediate state for callers.
      const r = await call<AuthResponse>("InitiateAuth", {
        ClientId: cfg.clientId,
        AuthFlow: "USER_AUTH",
        AuthParameters: { USERNAME: username, PREFERRED_CHALLENGE: "WEB_AUTHN" },
      });
      const first = await settle(r, username);
      if (first.status !== "challenge" || first.challenge !== "WEB_AUTHN") return first;
      const optionsJson = first.parameters?.CREDENTIAL_REQUEST_OPTIONS;
      if (!optionsJson) {
        throw new AuthError("WebAuthnNoOptions", "WEB_AUTHN challenge carried no request options");
      }
      const publicKey = pkc().parseRequestOptionsFromJSON(JSON.parse(optionsJson));
      const assertion = await navigator.credentials.get({ publicKey });
      return mod.respondToChallenge({
        username,
        challenge: "WEB_AUTHN",
        session: first.session,
        credential: credentialJson(assertion),
      });
    },

    async registerPasskey(accessToken) {
      // Enroll THIS device/provider as a passkey for the signed-in user.
      // Start → navigator.credentials.create → Complete.
      const start = await call<{ CredentialCreationOptions?: unknown }>(
        "StartWebAuthnRegistration",
        { AccessToken: accessToken },
      );
      if (!start.CredentialCreationOptions) {
        throw new AuthError("WebAuthnNoOptions", "StartWebAuthnRegistration returned no options");
      }
      const publicKey = pkc().parseCreationOptionsFromJSON(start.CredentialCreationOptions);
      const cred = await navigator.credentials.create({ publicKey });
      await call("CompleteWebAuthnRegistration", {
        AccessToken: accessToken,
        // The API takes the RegistrationResponseJSON as a JSON document.
        Credential: JSON.parse(credentialJson(cred)),
      });
    },

    async respondToChallenge(input) {
      const responses: Record<string, string> = { USERNAME: input.username };
      if (input.challenge === "WEB_AUTHN") {
        if (!input.credential) throw new AuthError("MissingInput", "credential is required");
        responses.CREDENTIAL = input.credential;
      } else if (input.challenge === "NEW_PASSWORD_REQUIRED") {
        if (!input.newPassword) throw new AuthError("MissingInput", "newPassword is required");
        responses.NEW_PASSWORD = input.newPassword;
        for (const [name, value] of Object.entries(input.userAttributes ?? {})) {
          responses[`userAttributes.${name}`] = value;
        }
      } else if (input.code) {
        // The response key is challenge-specific.
        const key =
          input.challenge === "SOFTWARE_TOKEN_MFA"
            ? "SOFTWARE_TOKEN_MFA_CODE"
            : input.challenge === "SMS_MFA"
              ? "SMS_MFA_CODE"
              : input.challenge === "EMAIL_OTP"
                ? "EMAIL_OTP_CODE"
                : "ANSWER";
        responses[key] = input.code;
      } else {
        throw new AuthError("MissingInput", `challenge ${input.challenge} needs a code`);
      }
      const r = await call<AuthResponse>("RespondToAuthChallenge", {
        ClientId: cfg.clientId,
        ChallengeName: input.challenge,
        Session: input.session,
        ChallengeResponses: responses,
      });
      return settle(r, input.username);
    },

    async signUp(username, password, attributes) {
      const pw = password ?? generateSignupPassword();
      // Email-mode pools REQUIRE an email-shaped username, so defaulting the
      // attribute from it is always right there. A handle username (no "@")
      // means a handle-mode pool — no email attribute exists to set.
      const attrs = {
        ...(username.includes("@") ? { email: username } : {}),
        ...(attributes ?? {}),
      };
      const r = await call<{ UserConfirmed?: boolean }>("SignUp", {
        ClientId: cfg.clientId,
        Username: username,
        Password: pw,
        UserAttributes: Object.entries(attrs).map(([Name, Value]) => ({ Name, Value })),
      });
      return { confirmed: Boolean(r.UserConfirmed), password: pw };
    },

    async confirmSignUp(username, code) {
      await call("ConfirmSignUp", {
        ClientId: cfg.clientId,
        Username: username,
        ConfirmationCode: code,
      });
    },

    async resendCode(username) {
      await call("ResendConfirmationCode", { ClientId: cfg.clientId, Username: username });
    },

    async forgotPassword(username) {
      await call("ForgotPassword", { ClientId: cfg.clientId, Username: username });
    },

    async confirmForgotPassword(username, code, newPassword) {
      await call("ConfirmForgotPassword", {
        ClientId: cfg.clientId,
        Username: username,
        ConfirmationCode: code,
        Password: newPassword,
      });
    },

    async enabledMethods() {
      try {
        const res = await fetch("/__s3p/auth.json", { cache: "no-cache" });
        if (!res.ok) throw new Error(String(res.status));
        const m = (await res.json()) as {
          password?: boolean;
          emailCode?: boolean;
          passkey?: boolean;
          usernameMode?: string;
          providers?: string[];
          signupOpen?: boolean;
        };
        return {
          password: m.password ?? true,
          emailCode: m.emailCode ?? false,
          passkey: m.passkey ?? false,
          usernameMode: m.usernameMode === "handle" ? ("handle" as const) : ("email" as const),
          providers: m.providers ?? [],
          signupOpen: m.signupOpen ?? true,
        };
      } catch {
        // No published config (older app, or not yet configured): the safe
        // default is the always-present password method + open signup.
        return {
          password: true,
          emailCode: false,
          passkey: false,
          usernameMode: "email" as const,
          providers: [],
          signupOpen: true,
        };
      }
    },

    async refresh(refreshToken) {
      const r = await call<AuthResponse>("InitiateAuth", {
        ClientId: cfg.clientId,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      });
      if (!r.AuthenticationResult?.IdToken) {
        throw new AuthError("NoTokens", "refresh returned no tokens");
      }
      const session = toSession(r.AuthenticationResult, refreshToken);
      await cfg.onSession(session);
      return session;
    },

    async signOut(refreshToken) {
      if (refreshToken) {
        await call("RevokeToken", { ClientId: cfg.clientId, Token: refreshToken }).catch(
          () => {}, // best-effort: local sign-out proceeds regardless
        );
      }
      cfg.onSignOut?.();
    },

    flow() {
      return createAuthFlow({
        auth: mod,
        startProviderRedirect:
          cfg.onProviderRedirect ??
          (() => {
            throw new AuthError(
              "NoProviderRedirect",
              "provider sign-in isn't configured on this auth module",
            );
          }),
      });
    },
  };

  return mod;
}
